import { PPFeature, PPLimit, PPProduct } from 'shared'
import { LIMIT_PROFILES_COUNT, LIMIT_UNLIMITED, LIMIT_USERS_COUNT } from 'shared/constants'
import { createSelector } from 'reselect'
// Imported from the module rather than the `services/settings` barrel, which pulls in
// `adapters` -> `services/product` and would close a circular dependency
import { accountSelector } from 'services/settings/selectors'
import { userProductSelector } from 'services/users/selectors'
import StoreState from 'store/state'
import { sortByKeyAscending } from 'utils/sort/order'
import { initialState, ProductState } from './state'

export const productStateSelector = (state: StoreState) => state.product || initialState()
export const productsSelector = createSelector(productStateSelector, state => {
  return [...state.products].sort(sortByKeyAscending('order'))
})
export const addOnsSelector = createSelector(productStateSelector, state => state.addons || [])
export const activeProductsSelector = createSelector(productStateSelector, state => {
  return [...state.products].sort(sortByKeyAscending('order')).filter(p => !p.legacy)
})
export const productsByHandleSelector = createSelector(productsSelector, products => {
  return products.reduce((map: Record<string, PPProduct>, product) => {
    map[product.handles.monthly] = product
    map[product.handles.annual] = product
    return map
  }, {})
})
export const featureUpgradeSelector = createSelector(productStateSelector, (state: ProductState) => state.featureUpgrade)
export const productUpgradeSelector = createSelector(productStateSelector, (state: ProductState) => state.productUpgrade)
export const onCloseUpgradeSelector = createSelector(productStateSelector, (state: ProductState) => state.closeUpgradePopupCallback)
export const contentUpgradePupupDismissedSelector = createSelector(productStateSelector, state => !state.displayContentUpgradePopup)
// Returns the next upgrade of user's current product.
// Matched by order rather than position - a legacy current product is not in the active list
export const userProductUpgradeSelector = createSelector(userProductSelector, activeProductsSelector, (current, all) => {
  if (!current) {
    return undefined
  }
  return all.find(p => p.order > current.order)
})
export const featuresSelector = createSelector(
  productStateSelector, state => state.features.reduce((map: { [key: string]: PPFeature }, feature: PPFeature) => {
    map[feature.key] = feature
    return map
  }, {})
)
export const limitsSelector = createSelector(
  productStateSelector, state => state.limits.reduce((map: { [key: string]: PPLimit }, limit: PPLimit) => {
    map[limit.key] = limit
    return map
  }, {})
)
/**
 * `PPProduct.limits` only ever holds the enforced cap - the -1 "unlimited" flags live in the
 * `PPLimit[]` dataset, keyed by plan. Cross-reference the two so callers can tell a limit that
 * is *labelled* unlimited from one that merely has a very high cap.
 *
 * Shape: `{ [productId]: { [limitKey]: true } }`
 */
export const unlimitedLimitsByProductSelector = createSelector(
  productStateSelector, state => state.limits.reduce((map: Record<string, Record<string, boolean>>, limit: PPLimit) => {
    // `plans` is absent on limits that were never given per-plan values
    Object.keys(limit.plans || {}).forEach((productId: string) => {
      if (limit.plans[productId] === LIMIT_UNLIMITED) {
        map[productId] = { ...map[productId], [limit.key]: true }
      }
    })
    return map
  }, {})
)

/**
 * Limit keys flagged as unlimited for the current user's product.
 * Empty until `getFeaturesAndLimits` resolves, so treat a miss as "not known to be unlimited".
 */
export const userUnlimitedLimitsSelector = createSelector(
  unlimitedLimitsByProductSelector,
  userProductSelector,
  (byProduct: Record<string, Record<string, boolean>>, product: PPProduct) => (product && byProduct[product.id]) || {}
)

/**
 * Whether the "unlimited" label should be shown for social accounts.
 * An account-level profiles override is a real enforced cap, so it wins over the plan's label.
 */
export const profilesUnlimitedSelector = createSelector(
  userUnlimitedLimitsSelector,
  accountSelector,
  (unlimitedLimits, account) => {
    return Boolean(unlimitedLimits[LIMIT_PROFILES_COUNT]) && !account?.subscription?.useCustomProfilesLimit
  }
)

/** Whether the "unlimited" label should be shown for team member slots */
export const usersUnlimitedSelector = createSelector(
  userUnlimitedLimitsSelector,
  unlimitedLimits => Boolean(unlimitedLimits[LIMIT_USERS_COUNT])
)

export const defaultProductUpgradeHandleSelector = createSelector(
  userProductUpgradeSelector,
  userProductSelector,
  (upgrade: PPProduct | undefined, current: PPProduct) => {
    return upgrade?.handles.annual || current?.handles.annual
  }
)

export const purchasedAddOnsSelector = createSelector(productStateSelector, state => state.purchasedAddOns || {})
// How many of each purchased add-on end at the next renewal, keyed by add-on id
export const pendingAddOnDropsSelector = createSelector(productStateSelector, state => state.pendingAddonDowngrades || {})
